/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application.
 * Handles user session management, login/logout, role-based access control,
 * and module-level permissions.
 */

/* eslint-disable react-refresh/only-export-components */
// Note: Fast refresh rule disabled because Context files need to export both
// the Provider component and the Context object. This only affects HMR during
// development and has no impact on production builds.

import React, { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '../lib/api';
import { useIdleTimer } from '../hooks/useIdleTimer';
import SessionTimeoutModal from '../components/SessionTimeoutModal';

// =============================================================================
// Types
// =============================================================================

/**
 * Module access levels (ordered by privilege)
 */
export type ModuleAccessLevel = 'none' | 'view' | 'edit' | 'admin';

/**
 * Module permissions map: moduleId -> accessLevel
 */
export type ModulePermissions = Record<string, ModuleAccessLevel>;

/**
 * Tool permissions map: moduleId -> { toolId -> accessLevel }
 */
export type ToolPermissions = Record<string, Record<string, ModuleAccessLevel>>;

/**
 * Feature flags exposed by the server.
 * Empty in the boilerplate; add product-specific flags here when needed.
 */
export type FeatureFlags = Record<string, boolean>;

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {};

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  modulePermissions?: ModulePermissions;
  toolPermissions?: ToolPermissions;
}

/**
 * Role hierarchy levels for access control.
 * Higher number = more permissions.
 */
const ROLE_LEVELS: Record<string, number> = {
  ZERO_ACCESS: 0,
  USER: 1,
  ADMIN: 2,
};

/**
 * Module access level hierarchy.
 * Higher number = more permissions.
 */
const MODULE_ACCESS_LEVELS: Record<ModuleAccessLevel, number> = {
  none: 0,
  view: 1,
  edit: 2,
  admin: 3,
};

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  devLogin: (email: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasAccessRole: (requiredRole: string) => boolean;
  isAdmin: boolean;
  // Module permissions
  modulePermissions: ModulePermissions;
  hasModuleAccess: (moduleId: string, requiredLevel?: ModuleAccessLevel) => boolean;
  getModuleAccessLevel: (moduleId: string) => ModuleAccessLevel;
  // Tool permissions
  toolPermissions: ToolPermissions;
  hasToolAccess: (moduleId: string, toolId: string, requiredLevel?: ModuleAccessLevel) => boolean;
  getToolAccessLevel: (moduleId: string, toolId: string) => ModuleAccessLevel;
  // Feature flags
  featureFlags: FeatureFlags;
}

// =============================================================================
// Context
// =============================================================================

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// =============================================================================
// Provider Component
// =============================================================================

// =============================================================================
// DEV MODE BYPASS - Mock user for development without backend
// =============================================================================
const DEV_BYPASS_AUTH = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

const MOCK_USER: User = {
  id: 'dev-user-001',
  email: 'admin@dev.local',
  name: 'מפתח (Admin)',
  role: 'ADMIN',
  modulePermissions: {
    finance: 'admin',
  },
  toolPermissions: {},
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(DEV_BYPASS_AUTH ? MOCK_USER : null);
  const [isLoading, setIsLoading] = useState(!DEV_BYPASS_AUTH);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);

  // ---------------------------------------------------------------------------
  // API Calls
  // ---------------------------------------------------------------------------

  const refreshUser = useCallback(async () => {
    // In bypass mode, set mock user but still fetch feature flags from server
    if (DEV_BYPASS_AUTH) {
      setUser(MOCK_USER);
      try {
        const { data } = await api.get<{ featureFlags?: FeatureFlags }>('/api/auth/feature-flags');
        if (data?.featureFlags) {
          setFeatureFlags(data.featureFlags);
        }
      } catch (error) {
        console.error('Failed to fetch feature flags in dev mode:', error);
      }
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await api.get<{ user: User; featureFlags?: FeatureFlags }>('/api/auth/me');

      if (!error && data?.user) {
        setUser(data.user);
        if (data.featureFlags) {
          setFeatureFlags(data.featureFlags);
        }
      } else {
        setUser(null);
        setFeatureFlags(DEFAULT_FEATURE_FLAGS);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      setFeatureFlags(DEFAULT_FEATURE_FLAGS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(() => {
    window.location.href = '/api/auth/google';
  }, []);

  const devLogin = useCallback(async (email: string, name?: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await api.post<{ user: User }>('/api/auth/dev-login', { email, name });

      if (!error && data?.user) {
        setUser(data.user);
      } else {
        throw new Error(error?.message || 'Login failed');
      }
    } catch (error) {
      console.error('Dev login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      localStorage.removeItem('lastActiveTime');
    } catch {
      // localStorage may be unavailable
    }
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Role-based Access Control
  // ---------------------------------------------------------------------------

  const hasAccessRole = useCallback((requiredRole: string): boolean => {
    if (!user) return false;
    const userLevel = ROLE_LEVELS[user.role] ?? 0;
    const requiredLevel = ROLE_LEVELS[requiredRole] ?? 0;
    return userLevel >= requiredLevel;
  }, [user]);

  // ---------------------------------------------------------------------------
  // Module Permissions
  // ---------------------------------------------------------------------------

  /**
   * Get the user's module permissions map.
   * Returns empty object if no user or no permissions.
   */
  const modulePermissions: ModulePermissions = user?.modulePermissions || {};

  /**
   * Get the access level for a specific module.
   * ADMIN users always have 'admin' level for all modules.
   */
  const getModuleAccessLevel = useCallback((moduleId: string): ModuleAccessLevel => {
    if (!user) return 'none';
    
    // ADMIN always has full access
    if (user.role === 'ADMIN') return 'admin';
    
    return (user.modulePermissions?.[moduleId] as ModuleAccessLevel) || 'none';
  }, [user]);

  /**
   * Check if user has required access level for a module.
   * 
   * @param moduleId - The module to check access for
   * @param requiredLevel - Minimum required level (default: 'view')
   * @returns true if user has sufficient access
   */
  const hasModuleAccess = useCallback((moduleId: string, requiredLevel: ModuleAccessLevel = 'view'): boolean => {
    const userLevel = getModuleAccessLevel(moduleId);
    return MODULE_ACCESS_LEVELS[userLevel] >= MODULE_ACCESS_LEVELS[requiredLevel];
  }, [getModuleAccessLevel]);

  // ---------------------------------------------------------------------------
  // Tool Permissions
  // ---------------------------------------------------------------------------

  /**
   * Get the user's tool permissions map.
   * Returns empty object if no user or no permissions.
   */
  const toolPermissions: ToolPermissions = user?.toolPermissions || {};

  /**
   * Get the access level for a specific tool within a module.
   * ADMIN users always have 'admin' level for all tools.
   */
  const getToolAccessLevel = useCallback((moduleId: string, toolId: string): ModuleAccessLevel => {
    if (!user) return 'none';
    
    // ADMIN always has full access
    if (user.role === 'ADMIN') return 'admin';
    
    return (user.toolPermissions?.[moduleId]?.[toolId] as ModuleAccessLevel) || 'none';
  }, [user]);

  /**
   * Check if user has required access level for a specific tool.
   * 
   * @param moduleId - The parent module ID
   * @param toolId - The tool ID to check access for
   * @param requiredLevel - Minimum required level (default: 'view')
   * @returns true if user has sufficient access
   */
  const hasToolAccess = useCallback((moduleId: string, toolId: string, requiredLevel: ModuleAccessLevel = 'view'): boolean => {
    const userLevel = getToolAccessLevel(moduleId, toolId);
    return MODULE_ACCESS_LEVELS[userLevel] >= MODULE_ACCESS_LEVELS[requiredLevel];
  }, [getToolAccessLevel]);

  // ---------------------------------------------------------------------------
  // Session Idle Timer
  // ---------------------------------------------------------------------------

  const isAuthenticated = !!user;

  const { isWarning, remainingSeconds, resetTimer } = useIdleTimer({
    enabled: isAuthenticated && !DEV_BYPASS_AUTH,
    onTimeout: logout,
  });

  // ---------------------------------------------------------------------------
  // Context Value
  // ---------------------------------------------------------------------------

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    devLogin,
    logout,
    refreshUser,
    hasAccessRole,
    isAdmin: user?.role === 'ADMIN',
    // Module permissions
    modulePermissions,
    hasModuleAccess,
    getModuleAccessLevel,
    // Tool permissions
    toolPermissions,
    hasToolAccess,
    getToolAccessLevel,
    // Feature flags
    featureFlags,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutModal
        isOpen={isWarning}
        remainingSeconds={remainingSeconds}
        onKeepBrowsing={resetTimer}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
};

// =============================================================================
// Re-export Hook (for backward compatibility)
// =============================================================================

export { useAuth } from '../hooks/useAuth';
